import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/session';
import type { OrganizationMembershipSummary } from '@/features/auth/types';
import {
  clearActiveOrganizationId,
  getActiveOrganizationId,
  setActiveOrganizationId,
} from '@/lib/api/active-org';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

type ActiveOrganizationStatus = 'loading' | 'selecting' | 'ready';

interface ActiveOrganizationContextValue {
  readonly status: ActiveOrganizationStatus;
  readonly memberships: readonly OrganizationMembershipSummary[];
  readonly activeOrganizationId: string | null;
  readonly activeMembership: OrganizationMembershipSummary | null;
  readonly select: (organizationId: string) => void;
}

const ActiveOrganizationContext = createContext<ActiveOrganizationContextValue | null>(null);

/**
 * The one centralized Active Organization layer (ADR-026 Addendum,
 * Decision AO-1), distinct from the auth session it reads from. A single
 * active membership auto-selects silently (AO-4); more than one, with no
 * valid stored selection, requires an explicit choice (AO-5) rather than
 * ever picking `memberships[0]`. A stored id is revalidated against the
 * user's current active memberships on every restoration - never trusted
 * merely because it exists (AO-6). Logging out, or any 401 that resets
 * the auth session, clears both the stored id and every tenant-scoped
 * query (AO-14).
 */
export function ActiveOrganizationProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const { status: authStatus, user } = useAuth();
  const queryClient = useQueryClient();
  const [activeOrganizationId, setActiveOrganizationIdState] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);

  const memberships = useMemo<readonly OrganizationMembershipSummary[]>(
    () => user?.organizationMemberships ?? [],
    [user],
  );

  const clearTenantQueries = useCallback((): void => {
    void queryClient.removeQueries({ queryKey: [TENANT_QUERY_PREFIX] });
  }, [queryClient]);

  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      clearActiveOrganizationId();
      setActiveOrganizationIdState(null);
      setResolved(false);
      clearTenantQueries();
      return;
    }

    if (authStatus !== 'authenticated') {
      return;
    }

    const storedId = getActiveOrganizationId();
    const storedIsValid =
      storedId !== null && memberships.some((membership) => membership.organizationId === storedId);

    if (storedIsValid) {
      setActiveOrganizationIdState(storedId);
    } else {
      if (storedId !== null) {
        clearActiveOrganizationId();
      }
      const onlyMembership = memberships.length === 1 ? memberships[0] : undefined;
      if (onlyMembership !== undefined) {
        setActiveOrganizationId(onlyMembership.organizationId);
        setActiveOrganizationIdState(onlyMembership.organizationId);
      } else {
        setActiveOrganizationIdState(null);
      }
    }
    setResolved(true);
    // `memberships` is derived from `user`; already covered below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, user]);

  const select = useCallback(
    (organizationId: string): void => {
      const isValid = memberships.some(
        (membership) => membership.organizationId === organizationId,
      );
      if (!isValid) {
        // Never trust an id that isn't one of the user's current active
        // memberships (AO-15) - a no-op protects against a forged or
        // stale caller rather than silently accepting it.
        return;
      }
      setActiveOrganizationId(organizationId);
      setActiveOrganizationIdState(organizationId);
      clearTenantQueries();
    },
    [memberships, clearTenantQueries],
  );

  const activeMembership = useMemo(
    () =>
      memberships.find((membership) => membership.organizationId === activeOrganizationId) ?? null,
    [memberships, activeOrganizationId],
  );

  const status: ActiveOrganizationStatus =
    authStatus !== 'authenticated' || !resolved
      ? 'loading'
      : activeOrganizationId === null && memberships.length > 1
        ? 'selecting'
        : 'ready';

  const value: ActiveOrganizationContextValue = {
    status,
    memberships,
    activeOrganizationId,
    activeMembership,
    select,
  };

  return (
    <ActiveOrganizationContext.Provider value={value}>
      {children}
    </ActiveOrganizationContext.Provider>
  );
}

export function useActiveOrganization(): ActiveOrganizationContextValue {
  const value = useContext(ActiveOrganizationContext);
  if (value === null) {
    throw new Error('useActiveOrganization must be used within an ActiveOrganizationProvider.');
  }
  return value;
}
